import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, Search, RefreshCw, Download, Printer, Eye, ArrowRight, 
  Loader2, CheckCircle2, ExternalLink, AlertCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';

const TaxInvoices = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [zohoStatus, setZohoStatus] = useState(null);
  const [showZohoSection, setShowZohoSection] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchZohoStatus();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/invoices/');
      setInvoices(data?.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchZohoStatus = async () => {
    try {
      const data = await apiCall('/api/auth/zoho/status');
      setZohoStatus(data);
    } catch {
      setZohoStatus({ is_connected: false });
    }
  };

  const handleConnectZoho = async () => {
    try {
      setConnecting(true);
      const data = await apiCall('/api/auth/zoho/authorize');
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      toast({ title: 'Connection Failed', description: 'Could not start Zoho connection.', variant: 'destructive' });
    } finally {
      setConnecting(false);
    }
  };

  const handleDownloadPDF = async (orderId, invoiceNumber) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${orderId}/pdf`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber || 'invoice'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Error', description: 'Failed to download PDF', variant: 'destructive' });
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (inv.invoice_number || '').toLowerCase().includes(q) ||
      (inv.order_number || '').toLowerCase().includes(q) ||
      (inv.customer_name || '').toLowerCase().includes(q) ||
      (inv.customer_email || '').toLowerCase().includes(q)
    );
  });

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <DashboardLayout>
      <div className="p-3 md:p-6 space-y-4 md:space-y-6" data-testid="tax-invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Tax Invoices</h1>
            <p className="text-gray-600 text-sm mt-1">GST-compliant invoices auto-generated for all orders</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchInvoices} data-testid="refresh-invoices-btn">
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by invoice #, order #, customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="invoice-search"
          />
        </div>

        {/* Invoice Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12 px-4">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No invoices generated yet</p>
                <p className="text-gray-400 text-sm mt-1">
                  Invoices are auto-generated when you view an order's invoice. You can also generate them retroactively.
                </p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/orders')}>
                  Go to Orders <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 md:p-4 font-medium text-gray-600 text-xs md:text-sm">Invoice #</th>
                      <th className="text-left p-3 md:p-4 font-medium text-gray-600 text-xs md:text-sm">Order</th>
                      <th className="text-left p-3 md:p-4 font-medium text-gray-600 text-xs md:text-sm hidden sm:table-cell">Customer</th>
                      <th className="text-left p-3 md:p-4 font-medium text-gray-600 text-xs md:text-sm">Amount</th>
                      <th className="text-left p-3 md:p-4 font-medium text-gray-600 text-xs md:text-sm hidden md:table-cell">Type</th>
                      <th className="text-left p-3 md:p-4 font-medium text-gray-600 text-xs md:text-sm hidden md:table-cell">Date</th>
                      <th className="text-right p-3 md:p-4 font-medium text-gray-600 text-xs md:text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredInvoices.map((inv) => (
                      <tr key={inv.invoice_number} className="hover:bg-gray-50" data-testid={`invoice-row-${inv.invoice_number}`}>
                        <td className="p-3 md:p-4">
                          <span className="font-medium text-indigo-600 text-sm">{inv.invoice_number}</span>
                        </td>
                        <td className="p-3 md:p-4">
                          <button 
                            onClick={() => navigate(`/orders/${inv.order_id}`)}
                            className="text-sm text-gray-700 hover:text-indigo-600 hover:underline"
                          >
                            {inv.order_number || inv.order_id?.slice(0, 8)}
                          </button>
                        </td>
                        <td className="p-3 md:p-4 hidden sm:table-cell">
                          <div className="text-sm text-gray-600 truncate max-w-[150px]">{inv.customer_name || '-'}</div>
                          {inv.customer_email && <div className="text-xs text-gray-400 truncate max-w-[150px]">{inv.customer_email}</div>}
                        </td>
                        <td className="p-3 md:p-4">
                          <span className="font-medium text-sm">{inv.total_amount ? `\u20B9${inv.total_amount.toLocaleString('en-IN')}` : '-'}</span>
                        </td>
                        <td className="p-3 md:p-4 hidden md:table-cell">
                          <Badge variant="outline" className={inv.invoice_title === 'TAX INVOICE' ? 'text-green-600 border-green-300' : 'text-amber-600 border-amber-300'}>
                            {inv.invoice_title === 'TAX INVOICE' ? 'Tax Invoice' : 'Bill of Supply'}
                          </Badge>
                        </td>
                        <td className="p-3 md:p-4 hidden md:table-cell">
                          <span className="text-sm text-gray-500">{formatDate(inv.generated_at)}</span>
                        </td>
                        <td className="p-3 md:p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => navigate(`/orders/${inv.order_id}/invoice`)} title="View Invoice" data-testid={`view-${inv.invoice_number}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(inv.order_id, inv.invoice_number)} title="Download PDF" data-testid={`download-${inv.invoice_number}`}>
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Zoho Integration (Optional — Collapsible) */}
        <Card className="border-dashed">
          <CardHeader className="cursor-pointer py-3 px-4" onClick={() => setShowZohoSection(!showZohoSection)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm font-medium text-gray-600">Zoho Invoice Sync (Optional)</CardTitle>
                {zohoStatus?.is_connected && (
                  <Badge variant="outline" className="text-green-600 border-green-300 gap-1 text-xs">
                    <CheckCircle2 className="h-3 w-3" /> Connected
                  </Badge>
                )}
              </div>
              {showZohoSection ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </div>
            <CardDescription className="text-xs mt-1">
              Sync invoices to your Zoho account for accounting. You must maintain items in Zoho with matching SKU codes.
            </CardDescription>
          </CardHeader>
          {showZohoSection && (
            <CardContent className="pt-0 px-4 pb-4">
              {zohoStatus?.is_connected ? (
                <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-800">Connected to: {zohoStatus.organization_name || 'Zoho Invoice'}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => window.open('https://invoice.zoho.in', '_blank')} className="text-green-700">
                    Open Zoho <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700">
                      Aniyor already generates GST-compliant invoices internally. Connect Zoho only if you need invoices 
                      synced for your chartered accountant or accounting software. You'll need to create all items in Zoho 
                      with matching SKU codes and keep them updated.
                    </p>
                  </div>
                  <Button onClick={handleConnectZoho} disabled={connecting} size="sm" variant="outline">
                    {connecting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                    Connect Zoho Invoice
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TaxInvoices;
